import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import Header from '../components/Header';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/email/contact-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        alert('Thank you for your message! We will get back to you soon.');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error sending your message. Please try again or contact us directly at keith.at.bandcam@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="bg-midnight text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">
            Contact Us
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Form and Info Section */}
      <section className="py-16 bg-midnight text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-sky text-black p-8 rounded-lg border-2 border-black">
              <h2 className="text-2xl font-bold mb-6 font-display">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="support">Technical Support</option>
                    <option value="billing">Billing Question</option>
                    <option value="partnership">School Partnership</option>
                    <option value="feedback">Feedback</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie resize-none"
                    placeholder="Tell us how we can help you..."
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 px-6 border-2 border-black font-bold rounded-lg flex items-center justify-center ${
                    isSubmitting
                      ? 'bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed'
                      : 'bg-goldie text-black hover:bg-opacity-90'
                  }`}
                >
                  <Send className="w-5 h-5 mr-2" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold mb-6 font-display">Get in Touch</h2>
                <p className="text-lg text-gray-300 mb-8">
                  We're here to help with any questions about BandCam.io, school partnerships, 
                  or your video orders. Reach out to us through any of the channels below.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-goldie p-3 rounded-full">
                    <Mail className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Email Support</h3>
                    <p className="text-gray-300 mb-1">keith.at.bandcam@gmail.com</p>
                    <p className="text-sm text-gray-400">Response within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-goldie p-3 rounded-full">
                    <Phone className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Phone Support</h3>
                    <p className="text-gray-300 mb-1">(512) 707-9316</p>
                    <p className="text-sm text-gray-400">Monday - Friday, 9 AM - 6 PM CST</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-goldie p-3 rounded-full">
                    <MapPin className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Office Address</h3>
                    <p className="text-gray-300 mb-1">TBD</p>
                    <p className="text-gray-300 mb-1">Austin, TX 78747</p>
                    <p className="text-sm text-gray-400">United States</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-goldie p-3 rounded-full">
                    <Clock className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Business Hours</h3>
                    <p className="text-gray-300 mb-1">Monday - Friday: 9 AM - 6 PM CST</p>
                    <p className="text-gray-300 mb-1">Saturday: 10 AM - 4 PM CST</p>
                    <p className="text-sm text-gray-400">Sunday: Closed</p>
                  </div>
                </div>
              </div>
              {/*
              <div className="bg-sky text-black p-6 rounded-lg border-2 border-black">
                <div className="flex items-center mb-4">
                  <MessageSquare className="w-6 h-6 mr-2" />
                  <h3 className="text-lg font-semibold">Live Chat</h3>
                </div>
                <p className="text-gray-700 mb-4">
                  Need immediate assistance? Our live chat is available during business hours.
                </p>
                <button className="bg-goldie text-black hover:bg-opacity-90 px-4 py-2 border-2 border-black font-bold rounded">
                  Start Chat
                </button>
              </div>
              */}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Preview Section */}
      <section className="py-16 bg-sky text-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6 font-display">Frequently Asked Questions</h2>
          <p className="text-lg text-gray-700 mb-8">
            Can't find what you're looking for? Check out our comprehensive FAQ section.
          </p>
          <Link
            to="/faq"
            className="btn bg-goldie text-black hover:bg-opacity-90 text-lg px-8 py-4 border-2 border-black font-bold"
          >
            View All FAQs
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-midnight text-white border-t-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="BandCam.io Logo" 
                  className="w-8 h-8 object-contain"
                />
                <span className="ml-2 text-xl font-bold">BandCam.io</span>
              </div>
              <p className="text-gray-300 mb-4">
                Connecting families with their school bands to create unforgettable personalized video messages for special occasions.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/schools" className="text-gray-300 hover:text-white">Find Your School</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/help" className="text-gray-300 hover:text-white">Help Center</Link></li>
                <li><Link to="/faq" className="text-gray-300 hover:text-white">FAQ</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; {new Date().getFullYear()} BandCam.io. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
